<template>
  <v-container fluid>
    <page-title icon="fas fa-fw fa-cabinet-filing">
      Edit Space
      <template v-slot:buttons>
        <v-chip v-if="space.is_expired"
                color="warning"
                label>
          Expired
        </v-chip>
        <template v-else>
          <v-switch v-model="form.is_active"
                    label=""
                    @change="updateStatus"
                    class="mt-0 mr-4"
                    :color="form.is_active ? 'success' : 'warning'"
                    hide-details>
          </v-switch>
          <v-chip :color="form.is_active ? 'success' : 'warning'"
                  label>
            {{ form.is_active ? 'Active' : 'Inactive' }}
          </v-chip>
        </template>
      </template>
    </page-title>

    <v-form @submit.prevent="submit">

      <v-card class="mb-4">
        <v-card-text>
          <v-text-field v-model="form.title"
                        label="Title"
                        placeholder="Required"
                        outlined
                        dense
                        :rules="[rules.required]"
                        :error-messages="form.errors.get('title')"
                        counter
                        maxlength="100"
                        clearable>
          </v-text-field>

          <v-text-field v-model="form.subtitle"
                        label="Subtitle"
                        placeholder="Optional"
                        outlined
                        dense
                        clearable
                        :error-messages="form.errors.get('subtitle')"
                        counter
                        maxlength="100">
          </v-text-field>

          <v-autocomplete v-model="form.locale"
                          label="Language"
                          outlined
                          dense
                          :items="locales"
                          item-value="id"
                          item-text="name"
                          :rules="[rules.required]"
                          :error-messages="form.errors.get('locale')">
          </v-autocomplete>

          <v-select v-model="form.share_theme_id"
                    label="Theme"
                    outlined
                    dense
                    :items="shareThemes"
                    item-value="id"
                    item-text="name"
                    hint="If you don't choose a specific theme, your company's default theme will be used."
                    persistent-hint
                    clearable
                    class="mb-4">
          </v-select>

          <v-select v-model="form.layout"
                    label="Layout"
                    outlined
                    dense
                    hint="Do you want the presentations to be displayed in a list or a grid?"
                    persistent-hint
                    :items="['List', 'Grid']"
                    class="mb-4">
          </v-select>

          <div class="d-flex align-center">
            <v-menu v-model="datePicker"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                    min-width="auto">
              <template v-slot:activator="{on, attrs}">
                <v-text-field v-model="form.expires_at"
                              type="date"
                              label="Expiration date"
                              outlined
                              dense
                              readonly
                              clearable
                              v-bind="attrs"
                              v-on="on"
                              :error-messages="form.errors.get('expires_at')"
                              hint="If set, this space will become inactive after this date."
                              persistent-hint
                              class="mb-4">
                </v-text-field>
              </template>
              <v-date-picker v-model="form.expires_at"
                             @input="datePicker = false">
              </v-date-picker>
            </v-menu>
          </div>

          <v-autocomplete v-model="form.groups"
                          label="Which groups can share this space via email?"
                          outlined
                          dense
                          multiple
                          clearable
                          chips
                          small-chips
                          deletable-chips
                          :items="groups"
                          item-value="id"
                          item-text="name"
                          hint="If this is empty, nobody will be able to share this space via email."
                          persistent-hint>
          </v-autocomplete>
        </v-card-text>
        <v-card-actions class="px-4">
          <v-btn v-if="showAdvancedSettings"
                 @click.prevent="showAdvancedSettings = false"
                 text
                 outlined>
            Hide advanced settings
          </v-btn>
          <v-btn v-else
                 @click.prevent="showAdvancedSettings = true"
                 text
                 outlined>
            Show advanced settings
          </v-btn>
        </v-card-actions>
        <template v-if="showAdvancedSettings">
          <v-card-title v-if="1===2"
                        class="pb-0">
            Advanced Settings
          </v-card-title>
          <v-card-text class="pt-0 pb-0">
            <v-switch v-model="form.search_enabled"
                      label="Display a search box at the top of the space"
                      hint="Turn this off if you don't want people to be able to search for content in the space."
                      persistent-hint
                      class="mb-8">
            </v-switch>

            <v-autocomplete v-model="form.fragment_id"
                            label="Fragment"
                            outlined
                            dense
                            :items="fragments"
                            item-value="id"
                            item-text="name"
                            clearable
                            :error-messages="form.errors.get('fragment_id')">
            </v-autocomplete>

            <v-select v-if="form.fragment_id"
                      v-model="form.fragment_position"
                      label="Fragment position"
                      outlined
                      dense
                      :items="fragmentPositions"
                      item-value="value"
                      item-text="text"
                      :error-messages="form.errors.get('fragment_position')">
            </v-select>

            <v-switch v-model="form.show_copyright_statement"
                      label="Display a copyright statement in the footer"
                      hint="Turn this off if you don't want to display a copyright statement in the footer of the space."
                      persistent-hint
                      class="mb-4">
            </v-switch>

            <v-text-field v-if="form.show_copyright_statement"
                          v-model="form.copyright_statement"
                          label="Copyright statement"
                          :placeholder="`Enter your copyright statement here. Don't include ${copyrightPrefix}`"
                          outlined
                          dense
                          :prefix="copyrightPrefix"
                          counter
                          maxlength="255"
                          clearable
                          :error-messages="form.errors.get('copyright_statement')">
            </v-text-field>

            <v-switch v-model="form.show_privacy_policy"
                      label="Display a link to your privacy policy in the footer"
                      hint="Turn this off if you don't want to display a link to your company's privacy policy in the footer of the space."
                      persistent-hint
                      class="mb-4">
            </v-switch>

            <v-text-field v-if="form.show_privacy_policy"
                          v-model="form.privacy_policy_url"
                          type="url"
                          label="Privacy policy URL"
                          placeholder="It must start with https://"
                          outlined
                          dense
                          hint="It must start with https://"
                          clearable
                          :error-messages="form.errors.get('privacy_policy_url')">
            </v-text-field>
          </v-card-text>
        </template>
        <v-card-actions class="px-4">
          <v-btn type="submit"
                 color="primary">
            Update
          </v-btn>
        </v-card-actions>
      </v-card>

    </v-form>

    <v-row>
      <v-col cols="9">
        <v-card class="mb-4">
          <v-card-text>

            <AddPresentationToSpaceForm :space="space"
                                        :assets="assets"
                                        :busy="form.busy"
                                        @added="fetchData">
            </AddPresentationToSpaceForm>

            <SpaceAssetList v-if="assets.length > 0"
                            :assets="assets"
                            @updated="fetchData"
                            @reordered="changeAssetOrder">
            </SpaceAssetList>
          </v-card-text>
        </v-card>

        <v-alert v-if="presentationAssets.length === 0"
                 type="warning">
          You haven't added any presentations yet.
        </v-alert>
      </v-col>
      <v-col cols="3">
        <v-card class="mb-4">
          <v-card-text>
            <v-btn :href="space.preview_url"
                   target="_blank"
                   block
                   class="mb-4">
              <v-icon left>
                fal fa-fw fa-external-link
              </v-icon>
              Preview space
            </v-btn>

            <v-btn @click.prevent="copyURL"
                   block
                   class="mb-4">
              <v-icon left>
                fal fa-fw fa-copy
              </v-icon>
              Copy URL
            </v-btn>

            <v-btn block
                   @click.prevent="duplicateSpace"
                   class="mb-4">
              <v-icon left>
                fal fa-copy
              </v-icon>
              Duplicate space
            </v-btn>

            <v-btn block
                   color="error"
                   @click.prevent="deleteSpace">
              <v-icon left>
                fas fa-trash
              </v-icon>
              Delete space
            </v-btn>
          </v-card-text>
        </v-card>

        <v-card v-if="space.is_active">
          <v-card-title class="pb-0">
            QR Code
          </v-card-title>
          <v-card-text class="pb-0">
            <a :href="space.qr_url"
               target="_blank">
              <img :src="space.qr_url"
                   alt="QR Code"
                   width="100%"/>
            </a>
          </v-card-text>
          <v-card-actions class="px-4">
            <v-btn block
                   color="primary"
                   @click.prevent="downloadQRCode">
              Download QR Code
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>

  </v-container>
</template>
<script>
import {Form} from 'vform';
import {find, isObject} from 'lodash';
import formValidation from '../../plugins/formValidation';
import spaceService from '../../services/spaceService';
import {mapGetters} from 'vuex';
import AddPresentationToSpaceForm from './components/AddPresentationToSpaceForm.vue';
import SpaceAssetList from './components/SpaceAssetList.vue';
import moment from 'moment-timezone';
import DOMPurify from 'dompurify';
import PageTitle from '../../components/PageTitle.vue';
import clipboardService from '../../../../ml/services/clipboardService';

export default {
  components: {PageTitle, SpaceAssetList, AddPresentationToSpaceForm},
  mixins: [formValidation],
  props: {
    space: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      assets: [],
      busy: false,
      datePicker: false,
      form: new Form({
        title: this.space.title,
        subtitle: this.space.subtitle,
        layout: this.space.layout,
        locale: this.space.locale, // MLD-5550
        expires_at: this.space.expires_at ? moment(this.space.expires_at).format('YYYY-MM-DD') : null,
        is_active: this.space.is_active,
        share_theme_id: this.space.share_theme_id ?? '',
        groups: this.space.groups ?? [],
        require_email: this.space.require_email,
        verify_email: this.space.verify_email,
        search_enabled: this.space.is_search_enabled,
        show_copyright_statement: this.space.show_copyright_statement,
        copyright_statement: this.space.copyright_statement,
        show_privacy_policy: this.space.show_privacy_policy,
        privacy_policy_url: this.space.privacy_policy_url,
        fragment_id: this.space.fragment_id, // MLD-5030
        fragment_position: this.space.fragment_position, // MLD-7035
      }),
      fragments: [], // MLD-5030
      showAdvancedSettings: false,
      fragmentPositions: [ // MLD-7035
        {value: 'Top', text: 'Above the items.',},
        {value: 'Bottom', text: 'Below the items.',},
        {value: 'Footer', text: 'A floating footer at the bottom of the page',},
      ]
    }
  },
  computed: {
    ...mapGetters([
      'activePresentations',
      'appURL',
      'groups',
      'locales', // MLD-5550
      'shareThemes',
      'team',
      'user',
    ]),
    copyrightPrefix() {
      const date = new Date();
      return `© ${date.getFullYear()}`;
    },
    availablePresentations() {
      return this.activePresentations.filter(p => {
        return p.is_internal_use_only === false;
      })
    },
    defaultTheme() {
      return find(this.shareThemes, t => t.is_default);
    },
    fragment() { // MLD-5030
      return this.space?.fragment;
    },
    presentationAssets() {
      return this.assets.filter(a => a.is_presentation);
    },
  },
  beforeRouteUpdate(to, from, next) {
    //console.log('beforeRouteUpdate', from.params.spaceID, to.params.spaceID)
    //this.$emit('updated', to.params.spaceID);
    this.fetchData(to.params.spaceID);
    next();
  },
  watch: {
    space(space) {
      //console.log('watch space');
      this.initForm(space);
    }
  },
  created() {
    this.initForm(this.space);
    this.fetchData(this.space.id);
  },
  methods: {
    initForm(space) {
      this.form.reset();
      this.form.title = space.title;
      this.form.subtitle = space.subtitle;
      this.form.layout = space.layout;
      this.form.expires_at = space.expires_at ? moment(space.expires_at).format('YYYY-MM-DD') : null;
      this.form.is_active = space.is_active;
      this.form.share_theme_id = space.share_theme_id ?? '';
      this.form.groups = space.groups ?? [];
      this.form.require_email = space.require_email;
      this.form.verify_email = space.verify_email;
      this.form.search_enabled = space.is_search_enabled;
      this.form.show_copyright_statement = space.show_copyright_statement;
      this.form.copyright_statement = space.copyright_statement;
      this.form.show_privacy_policy = space.show_privacy_policy;
      this.form.privacy_policy_url = space.privacy_policy_url;

      this.form.fragment_id = space.fragment_id; // MLD-5030
    },
    fetchData(spaceID) {
      if (!spaceID) {
        spaceID = this.space.id;
      }
      this.busy = true;
      spaceService.getFragments()
        .then(fragments => {
          this.fragments = fragments;
        })
        .catch(() => {
        });

      return spaceService.getAssets(spaceID)
        .then(assets => {
          this.assets = assets;
        })
        .catch(() => {
        })
        .finally(() => {
          this.busy = false;
        })
    },
    changeAssetOrder(assets) {
      const assetIDs = assets.map(a => a.id);
      spaceService.changeAssetOrder(this.space.id, assetIDs)
        .then(() => {
          this.$toast.success('Presentation positions updated successfully.');
        })
        .catch(() => {
        })
    },
    updateStatus() {
      const form = {
        is_active: this.form.is_active,
      }
      return axios.put(`/api/spaces/${this.space.id}`, form)
        .then(response => {
          const result = response.data;
          if (result === false) {
            return;
          }
          this.form.is_active = result.is_active;
          if (result.is_active) {
            this.$toast.success('Space activated.');
          } else {
            this.$toast.warning('Space deactivated.');
          }
          this.$emit('updated', this.space.id);
        })
        .catch(() => {
          this.form.is_active = !this.form.is_active;
        })
    },
    submit() {
      if (this.form.busy) {
        return;
      }
      const form = this.form.data();
      form.groups = form.groups.map(g => isObject(g) ? g.id : g);
      this.form.put(`/api/spaces/${this.space.id}`, {data: form})
        .then(() => {
          this.$toast.success('Space updated.');
        })
        .catch(err => {
          if (err.response?.data?.message) {
            this.$toast.error(DOMPurify.sanitize(err.response?.data?.message));
          }
        })
        .finally(() => {
          this.form.busy = false;
        });
    },
    duplicateSpace() {
      if (this.form.busy) {
        return;
      }
      this.form.busy = true;
      return axios.post(`/api/spaces/${this.space.id}/duplicate`)
        .then(response => {
          const dupe = response.data;
          this.$toast.success('Space duplicated.');
          this.$emit('updated', dupe.id);
          return this.$router.push({name: 'admin.space.edit', params: {spaceID: dupe.id}})
        })
        .catch(err => {
          if (err.response?.data?.message) {
            this.$toast.error(DOMPurify.sanitize(err.response?.data?.message));
          }
        })
        .finally(() => {
          this.form.busy = false;
        })
    },
    async deleteSpace() {
      if (this.form.busy) {
        return;
      }
      if (confirm('Are you sure you want to delete this space?')) {
        return this.form.delete(`/api/spaces/${this.space.id}`)
          .then(() => {
            this.$toast.success('Space deleted.');
            return this.$router.replace({name: 'admin.spaces.index'});
          })
          .catch(err => {
            if (err.response?.data?.message) {
              this.$toast.error(DOMPurify.sanitize(err.response?.data?.message));
            }
          });
      }
    },
    copyURL() { // MLD-5686
      clipboardService.copy(this.space.view_url, false)
        .then(() => {
          this.$toast.success('Space URL copied to your clipboard.');
        })
    },
    downloadQRCode() { // MLD-5162
      const link = document.createElement('a');
      link.href = this.space.qr_url;
      link.download = `${this.space.slug}.png`; // Specify the default file name
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }
}
</script>
